import { useRef, useState, useEffect } from 'react';
import './OnboardingGuidePage.css';
import { Button } from '@/components/ui/button';
import { settingsApi, analyticsApi } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  Printer, Radio, Cpu, Bell, AlertTriangle, ArrowRight, 
  Shield, Users, Clock, TrendingDown, Phone, Mail,
  MessageSquare, CheckCircle, ChevronRight, Wifi, Activity,
  ThumbsUp, HelpCircle, Lock, FileText, Eye, SprayCan,
  Search, Smartphone, ArrowDown, Download
} from 'lucide-react';

/* ─── Flow Diagram SVG ─── */
function FlowDiagram() {
  return (
    <div className="flow-diagram" data-testid="flow-diagram">
      <div className="flow-step flow-step-sensor">
        <div className="flow-icon-wrap bg-blue-50 border-blue-200">
          <Radio className="h-7 w-7 text-blue-600" />
        </div>
        <span className="flow-label">Sensor</span>
        <span className="flow-sublabel">Mäter luften 24/7</span>
      </div>
      <ArrowRight className="flow-arrow text-slate-300" />
      <div className="flow-step flow-step-system">
        <div className="flow-icon-wrap bg-slate-50 border-slate-200">
          <Cpu className="h-7 w-7 text-slate-700" />
        </div>
        <span className="flow-label">AI-analys</span>
        <span className="flow-sublabel">Filtrerar falsklarm</span>
      </div>
      <ArrowRight className="flow-arrow text-slate-300" />
      <div className="flow-step flow-step-alarm">
        <div className="flow-icon-wrap bg-red-50 border-red-200">
          <AlertTriangle className="h-7 w-7 text-red-500" />
        </div>
        <span className="flow-label">Larm</span>
        <span className="flow-sublabel">Teams + push + ljud</span>
      </div>
      <ArrowRight className="flow-arrow text-slate-300" />
      <div className="flow-step flow-step-staff">
        <div className="flow-icon-wrap bg-green-50 border-green-200">
          <Users className="h-7 w-7 text-green-600" />
        </div>
        <span className="flow-label">Personal</span>
        <span className="flow-sublabel">Kontrollerar rummet</span>
      </div>
      <ArrowRight className="flow-arrow text-slate-300" />
      <div className="flow-step flow-step-feedback">
        <div className="flow-icon-wrap bg-violet-50 border-violet-200">
          <ThumbsUp className="h-7 w-7 text-violet-600" />
        </div>
        <span className="flow-label">Feedback</span>
        <span className="flow-sublabel">Systemet lär sig</span>
      </div>
    </div>
  );
}

/* ─── Example Alarm Card ─── */
function ExampleAlarmCard() {
  return (
    <div className="example-alarm-card" data-testid="example-alarm-card">
      <div className="alarm-card-header">
        <div className="alarm-severity-badge alarm-severity-high">HÖG</div>
        <span className="alarm-type">Vape-larm</span>
        <span className="alarm-time">14:32</span>
      </div>
      <div className="alarm-card-body">
        <div className="alarm-detail-row">
          <span className="alarm-detail-label">Rum</span>
          <span className="alarm-detail-value">Pojktoalett 2:a våningen</span>
        </div>
        <div className="alarm-detail-row">
          <span className="alarm-detail-label">Skola</span>
          <span className="alarm-detail-value">Södra skolan</span>
        </div>
        <div className="alarm-detail-row">
          <span className="alarm-detail-label">Konfidens</span>
          <div className="alarm-confidence-bar">
            <div className="alarm-confidence-fill" style={{ width: '87%' }} />
          </div>
          <span className="alarm-detail-value alarm-score">87%</span>
        </div>
      </div>
      <div className="alarm-card-actions-example">
        <div className="alarm-action-btn-example alarm-action-confirm">
          <CheckCircle className="h-4 w-4" />
          <span>Bekräfta vape</span>
        </div>
        <div className="alarm-action-btn-example alarm-action-spray">
          <SprayCan className="h-4 w-4" />
          <span>Spray</span>
        </div>
        <div className="alarm-action-btn-example alarm-action-nothing">
          <Search className="h-4 w-4" />
          <span>Inget hittades</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Quick Reference Card ─── */
function QuickReferenceCard() {
  return (
    <div className="quick-ref-card" data-testid="quick-reference">
      <h3 className="quick-ref-title">Snabbreferens — klipp ut och ha nära!</h3>
      <div className="quick-ref-grid">
        <div className="quick-ref-item quick-ref-red">
          <AlertTriangle className="h-4 w-4" />
          <div>
            <strong>VAPE-LARM</strong>
            <span>Gå till rummet direkt. Bekräfta eller avfärda i appen.</span>
          </div>
        </div>
        <div className="quick-ref-item quick-ref-orange">
          <Activity className="h-4 w-4" />
          <div>
            <strong>MISSTÄNKT</strong>
            <span>Kontrollera rummet. Kan vara vape eller spray.</span>
          </div>
        </div>
        <div className="quick-ref-item quick-ref-green">
          <CheckCircle className="h-4 w-4" />
          <div>
            <strong>EFTER KONTROLL</strong>
            <span>Tryck alltid på rätt knapp: Vape / Spray / Inget hittades.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Section Components ─── */
function SectionNumber({ num }) {
  return (
    <div className="section-number" aria-hidden="true">
      {num}
    </div>
  );
}

export default function OnboardingGuidePage() {
  const printRef = useRef(null);
  const [supportPhone, setSupportPhone] = useState('');
  const [supportEmail, setSupportEmail] = useState('');

  useEffect(() => {
    settingsApi.get().then(res => {
      setSupportPhone(res.data.support_phone || '');
      setSupportEmail(res.data.support_email || '');
    }).catch(() => {});
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await api.get('/onboarding/pdf', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vapelarm_personalguide.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      // fallback to print
      window.print();
    }
  };

  const handleDownloadPresentation = async () => {
    try {
      const res = await api.get('/presentation/pdf', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vapelarm_presentation.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast('Kunde inte ladda ner presentationen');
    }
  };

  return (
    <div className="onboarding-page" ref={printRef} data-testid="onboarding-guide">
      {/* Print & PDF Buttons (hidden in print) */}
      <div className="print-controls no-print">
        <Button onClick={handleDownloadPresentation} className="presentation-btn" variant="default" data-testid="download-presentation-btn">
          <Download className="h-4 w-4 mr-2" />
          Systempresentation
        </Button>
        <Button onClick={handleDownloadPdf} className="pdf-download-btn" variant="default" data-testid="download-pdf-btn">
          <Download className="h-4 w-4 mr-2" />
          Personalguide PDF
        </Button>
        <Button onClick={handlePrint} className="print-btn" variant="outline" data-testid="print-guide-btn">
          <Printer className="h-4 w-4 mr-2" />
          Skriv ut
        </Button>
      </div>

      {/* ═══ COVER / HEADER ═══ */}
      <header className="guide-header">
        <div className="guide-header-content">
          <div className="guide-logo">
            <Shield className="h-10 w-10 text-white" />
          </div>
          <h1 className="guide-title">Vapelarm Cloud</h1>
          <p className="guide-subtitle">
            Personalguide — allt du behöver veta
          </p>
          <p className="guide-intro">
            Så här fungerar ert AI-drivna vapningsdetekteringssystem — 
            från sensor till åtgärd och uppföljning.
          </p>
        </div>
        <div className="guide-header-decoration" />
      </header>

      {/* ═══ QUICK REFERENCE ═══ */}
      <QuickReferenceCard />

      {/* ═══ 1. HOW IT WORKS ═══ */}
      <section className="guide-section">
        <div className="section-header">
          <SectionNumber num="1" />
          <div>
            <h2 className="section-title">Så här fungerar det</h2>
            <p className="section-description">
              Diskreta sensorer mäter luftkvaliteten i realtid. När AI:n upptäcker 
              partiklar som matchar en e-cigarett skickas ett larm automatiskt — och
              din feedback gör systemet smartare varje dag.
            </p>
          </div>
        </div>
        <FlowDiagram />
      </section>

      {/* ═══ 2. WHAT TRIGGERS AN ALARM ═══ */}
      <section className="guide-section">
        <div className="section-header">
          <SectionNumber num="2" />
          <div>
            <h2 className="section-title">Vad utlöser ett larm?</h2>
            <p className="section-description">
              Systemet använder AI och avklingningsanalys för att skilja vapning 
              från deodorant, hårspray och städmedel. Bara riktiga vape-signaler ger larm.
            </p>
          </div>
        </div>
        <div className="triggers-grid">
          <div className="trigger-card trigger-card-alarm">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <div>
              <h4 className="trigger-title">Vape-larm (röd)</h4>
              <p className="trigger-text">Hög konfidens att någon vappar. Kräver omedelbar kontroll av rummet.</p>
            </div>
          </div>
          <div className="trigger-card trigger-card-suspect">
            <Activity className="h-5 w-5 text-amber-500" />
            <div>
              <h4 className="trigger-title">Misstänkt (orange)</h4>
              <p className="trigger-text">Systemet är osäkert. Kan vara vape, men kan också vara något annat. Kontrollera och ge feedback.</p>
            </div>
          </div>
          <div className="trigger-card trigger-card-clear">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div>
              <h4 className="trigger-title">Filtreras automatiskt</h4>
              <p className="trigger-text">Deodorant, hårspray och städmedel identifieras av AI:n genom partikelstorlek och avklingningshastighet. Inget larm skickas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 3. WHAT THE VALUES MEAN ═══ */}
      <section className="guide-section">
        <div className="section-header">
          <SectionNumber num="3" />
          <div>
            <h2 className="section-title">Vad värdena betyder</h2>
            <p className="section-description">
              Du behöver inte förstå tekniken — här är en enkel förklaring.
            </p>
          </div>
        </div>
        <div className="values-table-wrap">
          <table className="values-table">
            <thead>
              <tr>
                <th>Värde</th>
                <th>Vad det mäter</th>
                <th>Normalt</th>
                <th>Larm</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>PM2.5</strong></td>
                <td>Små partiklar i luften (vape-ånga)</td>
                <td className="value-normal">Under 15</td>
                <td className="value-alarm">Över 30</td>
              </tr>
              <tr>
                <td><strong>VOC</strong></td>
                <td>Kemiska ångor (glycerin, propylenglykol)</td>
                <td className="value-normal">Under 100</td>
                <td className="value-alarm">Över 200</td>
              </tr>
              <tr>
                <td><strong>Konfidens</strong></td>
                <td>Hur säkert systemet är på att det är vaping</td>
                <td className="value-normal">—</td>
                <td className="value-alarm">Över 75%</td>
              </tr>
              <tr>
                <td><strong>Allvarlighetsgrad</strong></td>
                <td>HÖG / MEDEL / LÅG</td>
                <td className="value-normal">—</td>
                <td className="value-alarm">HÖG = omedelbar åtgärd</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="values-footnote">
          Konfidenspoängen (0–100%) kalibreras automatiskt baserat på personalens feedback.
          Ju mer feedback ni ger, desto bättre blir systemet.
        </p>
      </section>

      {/* ═══ EXAMPLE ALARM ═══ */}
      <section className="guide-section guide-section-example">
        <h2 className="section-title section-title-centered">Så ser ett larm ut i personalappen</h2>
        <p className="section-description" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          Personal med rollen "Personal" ser denna vy med ljud, vibration och röd skärmblink vid nya larm.
        </p>
        <div className="example-alarm-wrapper">
          <ExampleAlarmCard />
        </div>
        <p className="example-note">
          De tre knapparna är det viktigaste — tryck alltid på rätt knapp efter att du kontrollerat rummet!
        </p>
      </section>

      {/* ═══ 4. WHAT TO DO ═══ */}
      <section className="guide-section page-break-before">
        <div className="section-header">
          <SectionNumber num="4" />
          <div>
            <h2 className="section-title">Steg för steg: när larmet går</h2>
            <p className="section-description">
              Så här agerar du som personal.
            </p>
          </div>
        </div>
        <div className="steps-list">
          <div className="step-item">
            <div className="step-num">1</div>
            <div className="step-content">
              <h4 className="step-title">Ta emot notisen</h4>
              <p className="step-text">
                Du får ett meddelande i <strong>Microsoft Teams</strong>, en
                <strong> push-notis</strong> på mobilen, och personalappen spelar upp ett 
                <strong> larmljud med vibration</strong>. Öppna appen för att se detaljer.
              </p>
            </div>
          </div>
          <div className="step-item">
            <div className="step-num">2</div>
            <div className="step-content">
              <h4 className="step-title">Gå till rummet</h4>
              <p className="step-text">
                Gå till <strong>rummet som anges i larmet</strong>. Se efter tecken på 
                e-cigarettanvändning: ånga, lukt, elever som försvinner snabbt.
              </p>
            </div>
          </div>
          <div className="step-item step-item-highlight">
            <div className="step-num step-num-important">3</div>
            <div className="step-content">
              <h4 className="step-title">Ge feedback i appen</h4>
              <p className="step-text">
                <strong>Detta steg är kritiskt!</strong> Tryck på rätt knapp:
              </p>
              <div className="step-feedback-options">
                <div className="step-feedback-opt step-fb-confirm">
                  <CheckCircle className="h-4 w-4" />
                  <div>
                    <strong>Bekräfta vape</strong> — du hittade bevis på vapning
                  </div>
                </div>
                <div className="step-feedback-opt step-fb-spray">
                  <SprayCan className="h-4 w-4" />
                  <div>
                    <strong>Spray (ej vape)</strong> — det var deodorant, hårspray eller städmedel
                  </div>
                </div>
                <div className="step-feedback-opt step-fb-nothing">
                  <Search className="h-4 w-4" />
                  <div>
                    <strong>Inget hittades</strong> — rummet var tomt eller inget misstänkt
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="step-item">
            <div className="step-num">4</div>
            <div className="step-content">
              <h4 className="step-title">Dokumentera vid behov</h4>
              <p className="step-text">
                Vid bekräftad vapning — följ skolans egna rutiner. Händelsen loggas automatiskt 
                i systemets <strong>ärendehantering</strong> och kan följas upp.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 5. FEEDBACK MATTERS ═══ */}
      <section className="guide-section">
        <div className="section-header">
          <SectionNumber num="5" />
          <div>
            <h2 className="section-title">Din feedback gör systemet smartare</h2>
            <p className="section-description">
              Varje gång du trycker på en knapp efter ett larm förbättras AI:ns träffsäkerhet.
            </p>
          </div>
        </div>
        <div className="feedback-explanation">
          <div className="feedback-how-it-works">
            <div className="feedback-step-item">
              <div className="feedback-step-icon">
                <ThumbsUp className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <h4 className="feedback-step-title">Du ger feedback</h4>
                <p className="feedback-step-text">Efter att du kontrollerat rummet trycker du på rätt knapp i appen.</p>
              </div>
            </div>
            <ArrowDown className="feedback-arrow text-slate-300" />
            <div className="feedback-step-item">
              <div className="feedback-step-icon">
                <Cpu className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="feedback-step-title">AI:n kalibreras</h4>
                <p className="feedback-step-text">Systemet jämför sin förutsägelse med ditt svar och justerar automatiskt.</p>
              </div>
            </div>
            <ArrowDown className="feedback-arrow text-slate-300" />
            <div className="feedback-step-item">
              <div className="feedback-step-icon">
                <TrendingDown className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="feedback-step-title">Falsklarmen minskar</h4>
                <p className="feedback-step-text">Med tiden lär sig systemet vad som är vape och vad som är spray i just era lokaler.</p>
              </div>
            </div>
          </div>
          <div className="feedback-callout">
            <strong>Varje svar räknas!</strong> Systemets träffsäkerhet visas i appen och 
            uppdateras i realtid. Ju mer feedback ni ger, desto färre falsklarm.
          </div>
        </div>
      </section>

      {/* ═══ 6. INCIDENT MANAGEMENT ═══ */}
      <section className="guide-section">
        <div className="section-header">
          <SectionNumber num="6" />
          <div>
            <h2 className="section-title">Ärendehantering</h2>
            <p className="section-description">
              Varje larm blir automatiskt ett ärende som kan spåras från detektion till avslut.
            </p>
          </div>
        </div>
        <div className="incident-flow">
          <div className="incident-stage">
            <div className="incident-stage-badge incident-badge-new">Nytt</div>
            <p>Ärende skapas automatiskt vid vape/misstänkt-larm</p>
          </div>
          <ChevronRight className="incident-arrow text-slate-300" />
          <div className="incident-stage">
            <div className="incident-stage-badge incident-badge-assigned">Tilldelat</div>
            <p>En ansvarig person tilldelas ärendet</p>
          </div>
          <ChevronRight className="incident-arrow text-slate-300" />
          <div className="incident-stage">
            <div className="incident-stage-badge incident-badge-investigating">Utreds</div>
            <p>Personal utreder och dokumenterar</p>
          </div>
          <ChevronRight className="incident-arrow text-slate-300" />
          <div className="incident-stage">
            <div className="incident-stage-badge incident-badge-closed">Stängt</div>
            <p>Ärendet stängs med utfall och uppföljning</p>
          </div>
        </div>
        <div className="incident-note">
          <FileText className="h-4 w-4 text-slate-500 shrink-0" />
          <p>Alla ärenden loggas med tidslinje — perfekt för uppföljning med rektor, föräldrar eller elevhälsa.</p>
        </div>
      </section>

      {/* ═══ 7. NOTIFICATIONS ═══ */}
      <section className="guide-section">
        <div className="section-header">
          <SectionNumber num="7" />
          <div>
            <h2 className="section-title">Så når larmen er personal</h2>
          </div>
        </div>
        <div className="notif-grid">
          <div className="notif-card notif-teams">
            <MessageSquare className="h-6 w-6 text-[#6264A7]" />
            <h3 className="notif-card-title">Microsoft Teams</h3>
            <span className="notif-badge notif-badge-primary">Primär kanal</span>
            <p className="notif-card-text">
              Larm skickas som kort i en Teams-kanal. Alla i kanalen ser 
              larm direkt.
            </p>
          </div>
          <div className="notif-card notif-push">
            <Smartphone className="h-6 w-6 text-amber-500" />
            <h3 className="notif-card-title">Personalappen</h3>
            <span className="notif-badge notif-badge-primary">Rekommenderad</span>
            <p className="notif-card-text">
              Installera som app på mobilen. Ljud, vibration och röd blink 
              vid nya larm. Feedback-knappar direkt i appen.
            </p>
          </div>
          <div className="notif-card notif-email">
            <Mail className="h-6 w-6 text-slate-500" />
            <h3 className="notif-card-title">E-post</h3>
            <span className="notif-badge notif-badge-secondary">Valfritt</span>
            <p className="notif-card-text">
              Kan aktiveras som komplement. Bra för dokumentation.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ 8. SYSTEM EFFECT ═══ */}
      <section className="guide-section">
        <div className="section-header">
          <SectionNumber num="8" />
          <div>
            <h2 className="section-title">Effekten över tid</h2>
            <p className="section-description">
              Det här är varför systemet fungerar.
            </p>
          </div>
        </div>
        <div className="effect-content">
          <div className="effect-chart" data-testid="effect-chart">
            <div className="effect-chart-title">Typisk larmkurva efter installation</div>
            <div className="effect-chart-visual">
              <svg viewBox="0 0 400 140" className="effect-svg">
                <line x1="50" y1="20" x2="50" y2="120" stroke="#e2e8f0" strokeWidth="1" />
                <line x1="50" y1="120" x2="380" y2="120" stroke="#e2e8f0" strokeWidth="1" />
                <line x1="50" y1="70" x2="380" y2="70" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4" />
                <line x1="50" y1="45" x2="380" y2="45" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4" />
                <text x="45" y="25" textAnchor="end" className="effect-axis-label">Många</text>
                <text x="45" y="75" textAnchor="end" className="effect-axis-label">Några</text>
                <text x="45" y="118" textAnchor="end" className="effect-axis-label">Få</text>
                <text x="80" y="135" textAnchor="middle" className="effect-axis-label">V1</text>
                <text x="160" y="135" textAnchor="middle" className="effect-axis-label">V4</text>
                <text x="270" y="135" textAnchor="middle" className="effect-axis-label">V8</text>
                <text x="370" y="135" textAnchor="middle" className="effect-axis-label">V12</text>
                <path 
                  d="M60,25 C100,28 120,40 160,60 C200,78 240,90 280,100 C320,106 360,110 380,112" 
                  fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"
                />
                <path 
                  d="M60,25 C100,28 120,40 160,60 C200,78 240,90 280,100 C320,106 360,110 380,112 L380,120 L60,120 Z" 
                  fill="url(#fadeGrad)" opacity="0.3"
                />
                <defs>
                  <linearGradient id="fadeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <text x="200" y="55" textAnchor="middle" className="effect-annotation">Avskräckningseffekt</text>
              </svg>
            </div>
          </div>
          <div className="effect-text-box">
            <TrendingDown className="h-6 w-6 text-green-600 shrink-0" />
            <div>
              <h4 className="effect-text-title">Larmen minskar — och det är meningen</h4>
              <p className="effect-text-body">
                De flesta skolor ser en tydlig <strong>minskning av larm inom 4–8 veckor</strong>. 
                Bara vetskapen om att sensorerna finns där räcker för att förhindra de flesta försök. 
                Det är den <strong>beteendeförändrande effekten</strong> — inte bara detektionen — 
                som gör systemet värdefullt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 9. PRIVACY ═══ */}
      <section className="guide-section">
        <div className="section-header">
          <SectionNumber num="9" />
          <div>
            <h2 className="section-title">Integritet och sekretess</h2>
            <p className="section-description">
              Viktigt att veta om hur systemet hanterar data.
            </p>
          </div>
        </div>
        <div className="privacy-grid">
          <div className="privacy-item">
            <Eye className="h-5 w-5 text-blue-600" />
            <div>
              <h4 className="privacy-title">Ingen kamera eller mikrofon</h4>
              <p className="privacy-text">Sensorerna mäter bara luftkvalitet — partiklar, gaser och fuktighet. Ingen bild eller ljud fångas.</p>
            </div>
          </div>
          <div className="privacy-item">
            <Lock className="h-5 w-5 text-blue-600" />
            <div>
              <h4 className="privacy-title">Ingen identifiering av individer</h4>
              <p className="privacy-text">Systemet kan inte avgöra vem som är i rummet. Det detekterar enbart att vapning sker — inte av vem.</p>
            </div>
          </div>
          <div className="privacy-item">
            <Shield className="h-5 w-5 text-blue-600" />
            <div>
              <h4 className="privacy-title">GDPR-vänligt</h4>
              <p className="privacy-text">Ingen persondata lagras av sensorerna. Systemet lagrar bara luftkvalitetsdata och anonymiserade larmhändelser.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 10. FAQ ═══ */}
      <section className="guide-section page-break-before">
        <div className="section-header">
          <SectionNumber num="10" />
          <div>
            <h2 className="section-title">Vanliga frågor</h2>
          </div>
        </div>
        <div className="faq-list">
          <div className="faq-item">
            <h4 className="faq-question">
              <HelpCircle className="h-4 w-4 text-slate-400 shrink-0" />
              Vad händer om det larmar på natten eller helgen?
            </h4>
            <p className="faq-answer">
              Larm loggas dygnet runt men ingen behöver agera utanför arbetstid. 
              Händelsen sparas i ärendehanteringen och kan följas upp nästa arbetsdag.
              Larm löses automatiskt efter 5 minuter om nivåerna normaliseras.
            </p>
          </div>
          <div className="faq-item">
            <h4 className="faq-question">
              <HelpCircle className="h-4 w-4 text-slate-400 shrink-0" />
              Kan sensorn skilja på deodorant och vaping?
            </h4>
            <p className="faq-answer">
              Ja! Systemet analyserar partikelstorlek (PM1/PM2.5-ratio), kemisk signatur (VOC), 
              fuktighet och avklingningshastighet. Deodorantspray har en helt annan profil 
              än e-cigarettånga och filtreras automatiskt bort.
            </p>
          </div>
          <div className="faq-item">
            <h4 className="faq-question">
              <HelpCircle className="h-4 w-4 text-slate-400 shrink-0" />
              Vad gör jag om jag får ett larm men är osäker?
            </h4>
            <p className="faq-answer">
              Kontrollera rummet. Om du inte hittar något trycker du på "Inget hittades". 
              Systemet lär sig av ditt svar. Det är alltid bättre att ge feedback 
              än att ignorera larmet.
            </p>
          </div>
          <div className="faq-item">
            <h4 className="faq-question">
              <HelpCircle className="h-4 w-4 text-slate-400 shrink-0" />
              Hur installerar jag personalappen på mobilen?
            </h4>
            <p className="faq-answer">
              <strong>Android:</strong> Öppna länken i Chrome. Tryck på menyn (...) och välj "Lägg till på startskärmen".<br />
              <strong>iPhone:</strong> Öppna länken i Safari. Tryck på Dela-knappen och välj "Lägg till på hemskärmen".
              Appen fungerar sedan som en vanlig app med push-notiser.
            </p>
          </div>
          <div className="faq-item">
            <h4 className="faq-question">
              <HelpCircle className="h-4 w-4 text-slate-400 shrink-0" />
              Ser eleverna sensorerna?
            </h4>
            <p className="faq-answer">
              Sensorerna är små och diskreta. Många skolor väljer att synliggöra dem med en 
              skylt som informerar om övervakning av luftkvalitet. Redan vetskapen om att 
              sensorerna finns har avskräckande effekt.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ 11. SUPPORT ═══ */}
      <section className="guide-section guide-section-support">
        <div className="section-header">
          <SectionNumber num="11" />
          <div>
            <h2 className="section-title">Support och kontakt</h2>
            <p className="section-description">
              Vi hjälper er att få ut det bästa av systemet.
            </p>
          </div>
        </div>
        <div className="support-grid">
          <div className="support-card">
            <Phone className="h-5 w-5 text-blue-600" />
            <div>
              <h4 className="support-card-title">Telefon</h4>
              <p className="support-card-value">{supportPhone || 'Ej konfigurerad'}</p>
            </div>
          </div>
          <div className="support-card">
            <Mail className="h-5 w-5 text-blue-600" />
            <div>
              <h4 className="support-card-title">E-post</h4>
              <p className="support-card-value">{supportEmail || 'Ej konfigurerad'}</p>
            </div>
          </div>
        </div>
        <div className="support-note">
          <CheckCircle className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
          <p>
            Vi hjälper er att <strong>justera känsligheten</strong> för just era lokaler. 
            Kontakta oss om ni upplever för många eller för få larm — vi trimmar systemet 
            så att det passar er skola.
          </p>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="guide-footer">
        <p>Vapelarm Cloud SaaS-plattform — Konfidentiellt</p>
      </footer>
    </div>
  );
}
